/**
 * Decision 0146 (memory phase 2, L2): claims with validity, superseded instead of deleted. Pure.
 *
 * A memory item is **current** while it is accepted, nothing supersedes it and its `validTo` (if any) has not passed.
 * A superseded claim stays in the log and the index (greyed): it answers "what was true when" through
 * {@link validAt}, and its `supersededBy` / `supersedes` keep the chain both ways.
 */
import type { MemoryItem, ProjectState, Run } from "../types"

type Validity = Pick<
  MemoryItem,
  "status" | "supersededBy" | "validFrom" | "validTo" | "createdAt" | "content"
>

/** When the claim started being true: `validFrom`, else a decision's date, else when it was written. */
export const validFromOf = (m: Pick<MemoryItem, "validFrom" | "createdAt" | "content">): number =>
  m.validFrom ?? (m.content._tag === "decision" ? m.content.decidedAt : m.createdAt)

/** Is the claim in effect at `at` (accepted, not superseded, `validTo` not passed)? `at` omitted: ignores dates. */
export const isCurrentMemory = (m: Validity, at?: number): boolean => {
  if (m.status !== "accepted" || m.supersededBy !== undefined) return false
  if (at === undefined) return true
  return m.validTo === undefined || m.validTo > at
}

/**
 * Was the claim true at `at`? Accepted or superseded claims (never forgotten or revoked ones) whose
 * [validFrom, validTo) holds `at`. A superseded claim's `validTo` is when its successor took over.
 */
export const validAt = (m: Validity, at: number): boolean => {
  if (m.status !== "accepted") return false
  if (validFromOf(m) > at) return false
  return m.validTo === undefined || m.validTo > at
}

/** The person whose claim this is (they wrote it in Minga or through their CLI), or null for an agent's. */
export const personClaimOf = (m: Pick<MemoryItem, "createdBy">): string | null =>
  m.createdBy._tag === "user" ? m.createdBy.userId : null

/** The chain of a claim, oldest first (its predecessors, it, its successors), bounded against cycles. */
export const supersessionChain = (s: ProjectState, m: MemoryItem): ReadonlyArray<MemoryItem> => {
  const byId = new Map(s.memory.map((x) => [x.id as string, x]))
  const back: MemoryItem[] = []
  let cur: MemoryItem | undefined = m
  const seen = new Set<string>([m.id])
  while (cur?.supersedes !== undefined && back.length < 50) {
    const prev = byId.get(cur.supersedes)
    if (!prev || seen.has(prev.id)) break
    seen.add(prev.id)
    back.unshift(prev)
    cur = prev
  }
  const forward: MemoryItem[] = []
  cur = m
  while (cur?.supersededBy !== undefined && forward.length < 50) {
    const next = byId.get(cur.supersededBy)
    if (!next || seen.has(next.id)) break
    seen.add(next.id)
    forward.push(next)
    cur = next
  }
  return [...back, m, ...forward]
}

/** "valid 1 Oct 2026 – 14 Oct 2026" / "valid from 1 Oct 2026", in words for `read` and the index (UTC dates). */
export const validityWords = (
  m: Pick<MemoryItem, "validFrom" | "validTo" | "createdAt" | "content">,
): string | null => {
  if (m.validFrom === undefined && m.validTo === undefined) return null
  const d = (t: number) => new Date(t).toISOString().slice(0, 10)
  if (m.validTo === undefined) return `valid from ${d(validFromOf(m))}`
  return `valid ${d(validFromOf(m))} – ${d(m.validTo)}`
}

// ---- decision 0146: who may read a person's private memory -------------------------------------------------------

type TurnLike = Pick<Run, "kind" | "agentId" | "ownerId" | "askedBy">

/**
 * The person whose private memory an agent's turn may read, or null. Their own agents in their own turns, in a project
 * only they are in: the agent is theirs (`ownerId`), the turn serves their ask, and the project's roster is them alone
 * (a second member, or a workspace owner or admin in the roster, would see the turn's transcript and chat; wave E's
 * rule for personal memory, decision 0063). Workers and validators never read it.
 */
export const personalReaderOfTurn = (s: ProjectState, run: TurnLike | null | undefined): string | null => {
  if (run?.kind !== "turn" || run.agentId === null) return null
  const asker = run.askedBy?._tag === "user" ? run.askedBy.userId : null
  if (asker === null || run.ownerId !== asker) return null
  const agent = s.agents.find((a) => a.id === run.agentId)
  if (!agent || agent.ownerId !== asker) return null
  if (s.members.length !== 1 || s.members[0]?.userId !== asker) return null
  return asker
}

/**
 * The person an agent's turn may write private memory for: the person whose ask the turn serves (their own words, in
 * their own conversation). Writing reveals nothing; reading is {@link personalReaderOfTurn}'s narrower rule.
 */
export const personalWriterOfTurn = (run: TurnLike | null | undefined): string | null => {
  if (run?.kind !== "turn") return null
  return run.askedBy?._tag === "user" ? run.askedBy.userId : null
}
